import type { ComponentProps } from 'react';
import type {
  ProjectEventFamily,
  ProjectUndeliveredFamilies,
  ProjectUndeliveredFamily,
  ProjectUndeliveredReason,
} from '@avisoma/shared';
// Value-imported from the compiled file directly, NOT the `@avisoma/shared` barrel: the barrel's
// index re-exports every module as one graph, including `RequestSignature.ts` (Node `crypto`/
// `http`), so a runtime import off the barrel drags that into the Vite bundle. Same reason
// `roles` is imported from `@avisoma/shared/dist/roles.js` in app/src/pages/Users/*.
import {
  PROJECT_EVENT_FAMILIES as SHARED_PROJECT_EVENT_FAMILIES,
  PROJECT_UNDELIVERED_REASONS as SHARED_UNDELIVERED_REASONS,
} from '@avisoma/shared/dist/database/project.schema.js';
import type { Badge } from '../../catalyst/badge';
import { formatRelativeTime } from '../../lib/dateFormat';

type BadgeColor = NonNullable<ComponentProps<typeof Badge>['color']>;

/**
 * Single source of truth for how an outstanding Oil debt is presented (DR-2855).
 * The projects list badge, the project detail rows and the admin sweep table all
 * read labels/colours from here — never inline their own literals.
 */

/**
 * Keep the keys in emission order to match `PROJECT_EVENT_FAMILIES` below — a test pins the two
 * in step, since the render order now comes from that canonical list, not from this object.
 */
export const PROJECT_EVENT_FAMILY_LABELS: Record<ProjectEventFamily, string> = {
  engine: 'Engine',
  llp: 'LLP',
  ad: 'AD',
  sb: 'SB',
  ldnd: 'LDND',
  'bill-of-sale': 'Bill of Sale',
  qec: 'QEC',
  utilisation: 'Utilisation',
  'pma-der': 'PMA/DER',
  workscope: 'Engine Workscope',
  nis: 'NIS',
  'thrust-conversion': 'Thrust Conversion',
  'engine-movement': 'Engine Movement',
  arc: 'ARC',
  'tsn-csn-statement': 'Current TSN/CSN',
  'llp-btb': 'LLP Back-to-Birth',
  'build-record': 'Build Records',
  'module-exchange': 'Module Exchanges',
  'compliance-job-card-facts': 'Compliance job-card facts',
  'tracked-part-set': 'Tracked Part Sets',
  'engine-configuration-rows': 'Engine Configuration Rows',
  'removal-tags': 'Removal Tags',
  'no-exceedance': 'No Exceedance Statements',
  'oil-type': 'Engine Oil Type Statements',
};

/**
 * Emission order, single-sourced from `@avisoma/shared`'s canonical
 * {@link SHARED_PROJECT_EVENT_FAMILIES} (DR-2889) rather than derived from the labels Record
 * above. `PROJECT_EVENT_FAMILY_LABELS` still has to list every family (its own keyed-Record type
 * forces that), but the *order* rendered here now comes from one place instead of being able to
 * drift between this file and the server's `FAMILY_ORDER`. A test pins that the two stay
 * identical.
 */
export const PROJECT_EVENT_FAMILIES = SHARED_PROJECT_EVENT_FAMILIES;

export interface UndeliveredReasonMeta {
  label: string;
  color: BadgeColor;
  /** One-line explanation of what the operator is expected to do about it. */
  meaning: string;
  /**
   * Higher wins when picking the "worst" reason a project owes. This ranks how
   * bad the project's state is, NOT how urgent the operator's queue is:
   * `empty-snapshot` is the most severe because retry can never clear it, so it
   * outranks the retryable reasons. Do not invert it on the argument that
   * empty-snapshot markers bury actionable rows — that is about the sweep
   * list's volume, and the reason filter is what solves it.
   */
  severity: number;
}

export const UNDELIVERED_REASON_META: Record<ProjectUndeliveredReason, UndeliveredReasonMeta> = {
  'empty-snapshot': {
    label: 'Empty snapshot',
    color: 'red',
    meaning: 'Permanent. Needs the source data fixed; retry never clears it.',
    severity: 4,
  },
  'build-failed': {
    label: 'Build failed',
    color: 'orange',
    meaning: 'Generator bug. Needs a code fix.',
    severity: 3,
  },
  'delivery-failed': {
    label: 'Delivery failed',
    color: 'amber',
    meaning: 'Usually transient. Retry helps.',
    severity: 2,
  },
  'not-attempted': {
    label: 'Not attempted',
    color: 'amber',
    meaning: 'Same as delivery-failed — still owed, retry helps.',
    severity: 1,
  },
};

/**
 * Reason filter options, worst-first — single-sourced from `@avisoma/shared`'s canonical
 * {@link SHARED_UNDELIVERED_REASONS} (DR-2889), same as {@link PROJECT_EVENT_FAMILIES} above.
 * `UNDELIVERED_REASON_META` still has to list every reason (its keyed-Record type forces that,
 * and its declaration order happens to already be worst-first) — a test pins the two in step.
 * The server half single-sources the same way
 * (`server/src/routes/projects/undelivered-events.ts`).
 */
export const UNDELIVERED_REASONS = SHARED_UNDELIVERED_REASONS;

// `undelivered_families` is a jsonb blob, so a row written by a newer server can
// carry a reason this build has never heard of. Render it rather than crashing.
const UNKNOWN_REASON_META: UndeliveredReasonMeta = {
  label: 'Unknown reason',
  color: 'zinc',
  meaning: 'The server recorded a reason this app build does not recognise.',
  severity: 0,
};

// A Map rather than an indexed lookup on the Record: `/api/projects` and
// `/api/projects/:id` cast the raw jsonb without sanitising it, so a stored
// reason of `"toString"` would resolve through Object.prototype to a truthy
// Function. A `??` fallback would never fire — the badge would render
// uncoloured with an `undefined` label, and the bogus `undefined` severity
// would also stop a co-owed `empty-snapshot` winning the colour vote.
const REASON_META_BY_KEY = new Map<string, UndeliveredReasonMeta>(
  Object.entries(UNDELIVERED_REASON_META),
);

export function undeliveredReasonMeta(reason: ProjectUndeliveredReason): UndeliveredReasonMeta {
  return REASON_META_BY_KEY.get(reason) ?? UNKNOWN_REASON_META;
}

export interface UndeliveredFamilyEntry {
  family: ProjectEventFamily;
  /** Human label for the family, e.g. `bill-of-sale` → "Bill of Sale". */
  label: string;
  detail: ProjectUndeliveredFamily;
  meta: UndeliveredReasonMeta;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Mirrors `isValidEntry` in `server/src/utils/projectUndeliveredFamilies.ts`, which every
 * row of the sweep endpoint already passes through (`readUndeliveredFamilies`). `/api/projects`
 * and `/api/projects/:id` do NOT sanitise — they cast the raw jsonb — so a corrupt marker can
 * only reach the UI through those two. Dropping it here rather than rendering a partial entry
 * is what makes the surfaces agree: a marker that the sweep page can never show must not
 * conjure false debt (`… · undefined failures · first seen Invalid Date`) on the projects list.
 *
 * ONE deliberate divergence from the server reader: an unrecognised `reason` string is kept,
 * not dropped. A newer server writing a new reason is forward compatibility, not corruption —
 * `undeliveredReasonMeta` renders it via the unknown-reason fallback. Only structurally
 * invalid markers are dropped.
 */
function isValidMarker(value: unknown): value is ProjectUndeliveredFamily {
  return (
    isPlainObject(value) &&
    typeof value.reason === 'string' &&
    typeof value.message === 'string' &&
    typeof value.firstSeenAt === 'string' &&
    typeof value.occurredAt === 'string' &&
    typeof value.failures === 'number'
  );
}

/** Flattens the family map into emission-ordered rows. Empty when nothing is owed. */
export function listUndeliveredFamilies(
  families?: ProjectUndeliveredFamilies | null,
): UndeliveredFamilyEntry[] {
  if (!families) return [];

  return PROJECT_EVENT_FAMILIES.flatMap((family) => {
    const detail = families[family];
    if (!isValidMarker(detail)) return [];
    return [
      {
        family,
        label: PROJECT_EVENT_FAMILY_LABELS[family],
        detail,
        meta: undeliveredReasonMeta(detail.reason),
      },
    ];
  });
}

/** The most severe reason present, used to colour an aggregate badge. */
export function worstUndeliveredReasonMeta(
  entries: UndeliveredFamilyEntry[],
): UndeliveredReasonMeta | undefined {
  return entries.reduce<UndeliveredReasonMeta | undefined>(
    (worst, entry) => (!worst || entry.meta.severity > worst.severity ? entry.meta : worst),
    undefined,
  );
}

export function formatFailureCount(failures: number): string {
  return `${failures} ${failures === 1 ? 'failure' : 'failures'}`;
}

/** One line per owed family, for a native `title` tooltip. */
export function undeliveredFamiliesTooltip(entries: UndeliveredFamilyEntry[]): string {
  return entries
    .map(
      (entry) =>
        `${entry.label}: ${entry.meta.label} · ${formatFailureCount(entry.detail.failures)} · first seen ${formatRelativeTime(entry.detail.firstSeenAt)}`,
    )
    .join('\n');
}
